import * as React from 'react';
import { useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import TurndownService from 'turndown';

import { cn, ModalWrapperCloseIcon, sliceContent, useResponsiveBreakPoints } from '@restworld/utils-common';

import { BaseModal } from '../modal/base-modal';
import { Button } from '../button';
import { Heading, Text } from '@restworld/ui-ds-base';
import { BookOpenIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const turndownService = new TurndownService();

export const LongParagraph = ({
  subTitle,
  content,
  maxLength,
  title,
  headerIcon,
  btnTitle,
  isHtml,
  noSlice = false,
}: LongParagraphProps) => {
  const { open, close, show } = useModal();
  const { isDesktop } = useResponsiveBreakPoints();

  const makeVisible = useMemo(() => maxLength && content.length >= maxLength, [content, maxLength]);

  const contentMd = useMemo(() => (isHtml ? turndownService.turndown(content) : null), [content, isHtml]);

  const titleComponent = useMemo(
    () => (
      <span className="flex items-center justify-center gap-1">
        {headerIcon === 'BookOpenIcon' ? (
          <BookOpenIcon className="h-6 w-6" />
        ) : (
          <DocumentTextIcon className="h-6 w-6" />
        )}
        <Heading size={5} align="center" className="text-lg">
          {title}
        </Heading>
      </span>
    ),
    [headerIcon, title]
  );

  return (
    <>
      {subTitle && (
        // Match master's Title variant="h4-xsmall" (lg:text-xl / text-base, semibold);
        // the port shrank it to size={6} (text-sm), so it read as plain bold text.
        <Heading as="span" size={5} weight="semibold" className="lg:text-xl lg:leading-9">
          {subTitle}
        </Heading>
      )}
      <StyledBody>
        {noSlice && isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ) : contentMd ? (
          <Markdown>{sliceContent(contentMd, maxLength)}</Markdown>
        ) : (
          <Text size={3}>{sliceContent(content, maxLength)}</Text>
        )}
        {makeVisible && !noSlice ? (
          <ButtonWrapper>
            <Button
              icon="PlusSmallIcon"
              onClick={show ? close : open}
              variant="custom"
              title={btnTitle}
              buttonSize="small"
              isUnderLined={false}
              twStyle={'p-0 hover:underline hover:underline-offset-[5px]'}
            />
          </ButtonWrapper>
        ) : null}
      </StyledBody>
      <BaseModal
        open={show}
        onClose={close}
        showMobileGestures={true}
        variant="right"
        title={isDesktop ? titleComponent : undefined}
        closeIcon={<ModalWrapperCloseIcon />}
      >
        <div className="flex flex-col gap-4">
          {subTitle && (
            <Heading size={5} className="text-lg">
              {subTitle}
            </Heading>
          )}
          <StyledBody>
            {isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              <Text className="break-words text-left">{content}</Text>
            )}
          </StyledBody>
        </div>
      </BaseModal>
    </>
  );
};

export default LongParagraph;

export interface LongParagraphProps {
  subTitle?: string;
  content: string;
  maxLength?: number;
  title?: string;
  headerIcon: string;
  btnTitle: string;
  isHtml?: boolean;
  noSlice?: boolean;
}

const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      '[&>button]:gap-1 [&>button]:bg-inherit [&>button]:pl-0 [&>span]:w-5 [&>span]:h-5 mx-auto md:mx-0',
      className
    )}
    {...props}
  />
);

// The description is injected via dangerouslySetInnerHTML into a child <div>; these child
// selectors restore its prose spacing/lists. Written as Tailwind arbitrary variants
// ([&>div>p]:mb-2, no spaces, no parens) — the twin.macro `[> div > p]:(mb-2)` group syntax
// only compiles inside tw`` and was silently dropped once ported to a plain className.
// The selectors match descendants of that <div> (`_` is a space), not only its direct children:
// ATS descriptions (TeamTailor, e.g. Cigierre) wrap every paragraph and every list item in their
// own <div>, so direct-child rules never matched and the text rendered as one unspaced block with
// no bullets. A <p> inside an <li> keeps no bottom margin, so list items stay tight.
const StyledBody = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col gap-2 max-w-full overflow-hidden break-words [&>div]:overflow-hidden [&>div]:break-words [&>div_p]:mb-2 [&>div_li_p]:mb-0 [&>div_ul]:list-disc [&>div_ul]:my-2 [&>div_ul]:ml-6 [&>div_ul]:pl-2 [&>div_ul>li]:mb-1 [&>div_ol]:list-decimal [&>div_ol]:my-2 [&>div_ol]:ml-6 [&>div_ol]:pl-2 [&>div_ol>li]:mb-1',
      className
    )}
    {...props}
  />
);

// --> custom hook for modal
export const useModal = () => {
  const [show, toggleModal] = useState(false);
  const close = () => toggleModal(false);
  const open = () => toggleModal(true);
  return {
    show,
    close,
    open,
  };
};
